"use client"

import * as React from "react"
import { getDay, parse, startOfWeek, format } from "date-fns"
import { enUS, fr } from "date-fns/locale"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import {
  dateFnsLocalizer,
  Calendar,
  Views,
  type Event,
  type Formats,
  type Messages,
  type NavigateAction,
  type ToolbarProps,
  type View,
} from "react-big-calendar"

import { Button } from "./button"
import { Tabs, TabsList, TabsTrigger } from "./tabs"
import { cn } from "../../lib/utils"
import styles from "./app-calendar.module.css"
import "react-big-calendar/lib/css/react-big-calendar.css"

const locales = {
  en: enUS,
  fr,
}

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
})

export type AppCalendarEvent<TResource = unknown> = Event & {
  id: string
  resource?: TResource
}

function resolveViews<TResource>(
  views: ToolbarProps<AppCalendarEvent<TResource>, object>["views"]
) {
  if (Array.isArray(views)) return views

  const availableViews: View[] = []
  if (views.month) availableViews.push(Views.MONTH)
  if (views.week) availableViews.push(Views.WEEK)
  if (views.work_week) availableViews.push(Views.WORK_WEEK)
  if (views.day) availableViews.push(Views.DAY)
  if (views.agenda) availableViews.push(Views.AGENDA)
  return availableViews
}

type AppCalendarToolbarProps<TResource = unknown> =
  ToolbarProps<AppCalendarEvent<TResource>, object> & {
    viewLabels?: Partial<Record<View, string>>
    messages?: Messages<AppCalendarEvent<TResource>>
  }

function AppCalendarToolbar<TResource = unknown>({
  label,
  onNavigate,
  onView,
  view,
  views,
  messages,
  viewLabels,
}: AppCalendarToolbarProps<TResource>) {
  const availableViews = resolveViews(views)
  const resolvedMessages = messages ?? {}
  const todayLabel = String(resolvedMessages.today ?? "Today")
  const previousLabel = String(resolvedMessages.previous ?? "Back")
  const nextLabel = String(resolvedMessages.next ?? "Next")

  return (
    <div className={cn("rbc-toolbar", styles.toolbar)}>
      <div className={styles.toolbarLeft}>
        <Button
          type='button'
          size='icon-sm'
          variant='outline'
          onClick={() => onNavigate("PREV" as NavigateAction)}
          aria-label={previousLabel}
        >
          <ChevronLeftIcon className='size-4' />
        </Button>
        <Button
          type='button'
          size='sm'
          variant='outline'
          onClick={() => onNavigate("TODAY" as NavigateAction)}
        >
          {todayLabel}
        </Button>
        <Button
          type='button'
          size='icon-sm'
          variant='outline'
          onClick={() => onNavigate("NEXT" as NavigateAction)}
          aria-label={nextLabel}
        >
          <ChevronRightIcon className='size-4' />
        </Button>
      </div>

      <p className={styles.toolbarCenter}>{label}</p>

      <Tabs
        value={view}
        onValueChange={(nextValue) => onView(nextValue as View)}
        className={styles.toolbarRight}
      >
        <TabsList>
          {availableViews.map((calendarView) => (
            <TabsTrigger key={calendarView} value={calendarView}>
              {String(viewLabels?.[calendarView] ?? resolvedMessages[calendarView] ?? calendarView)}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
    </div>
  )
}

type AppCalendarProps<TResource = unknown> = {
  events: Array<AppCalendarEvent<TResource>>
  locale: "en" | "fr"
  className?: string
  minHeightClassName?: string
  messages?: Messages<AppCalendarEvent<TResource>>
  viewLabels?: Partial<Record<View, string>>
  formats?: Formats
  onSelectEvent?: (event: AppCalendarEvent<TResource>) => void
  renderEvent?: (event: AppCalendarEvent<TResource>) => React.ReactNode
}

export function AppCalendar<TResource = unknown>({
  events,
  locale,
  className,
  minHeightClassName = "min-h-[650px]",
  messages,
  viewLabels,
  formats,
  onSelectEvent,
  renderEvent,
}: AppCalendarProps<TResource>) {
  const eventComponent = React.useMemo(
    () =>
      function AppCalendarEventComponent({ event }: { event: AppCalendarEvent<TResource> }) {
        return <>{renderEvent ? renderEvent(event) : event.title}</>
      },
    [renderEvent]
  )

  const toolbarComponent = React.useMemo(
    () =>
      function ToolbarComponent(
        toolbarProps: ToolbarProps<AppCalendarEvent<TResource>, object>
      ) {
        return (
          <AppCalendarToolbar<TResource>
            {...toolbarProps}
            messages={messages}
            viewLabels={viewLabels}
          />
        )
      },
    [messages, viewLabels]
  )

  return (
    <div className={cn(styles.calendarRoot, className)}>
      <Calendar
        localizer={localizer}
        culture={locale}
        events={events}
        startAccessor='start'
        endAccessor='end'
        popup
        views={[Views.MONTH, Views.WEEK, Views.DAY, Views.AGENDA]}
        components={{ event: eventComponent, toolbar: toolbarComponent }}
        messages={messages}
        formats={formats}
        className={cn(minHeightClassName)}
        onSelectEvent={(event) => onSelectEvent?.(event as AppCalendarEvent<TResource>)}
      />
    </div>
  )
}
